import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { isLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/isLocaleEnglish';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts';
import { useSessionData } from '@confluence/session-data';

/**
 * Hook to determine if a user is eligible for the "Land in Sample Space Xflow" experiment.
 * Eligibility criteria:
 * - User must be in English locale
 * - User must be a desktop user
 * - User must be an Xflow user
 * - User must be a founding admin
 */
export const useIsEligibleLandInSampleSpaceXflow = () => {
	const { locale } = useSessionData();

	const isEnglishAndDesktopUser =
		isLocaleEnglish(locale) && !(isMobileUserAgent() || shouldShowMobileWeb());

	const {
		isXflowUser,
		error: userTypeError,
		loading: userTypeLoading,
	} = useUserByLicensedProducts({ skip: !isEnglishAndDesktopUser });

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN, ONBOARDING_EXPERIENCES.XFLOW_SPACE_CREATED],
		!isXflowUser,
	);

	const { isFoundingAdmin = false, xflowSpaceCreated = false } =
		deserializeState(onboardingStateData) || {};

	return {
		isEligible: isEnglishAndDesktopUser && isXflowUser && isFoundingAdmin,
		loading: userTypeLoading || onboardingStateLoading,
		error: userTypeError || onboardingStateError,
		xflowSpaceCreated: xflowSpaceCreated,
	};
};
